/**
 * "+X XP" flash (specs/xp-and-ranks): a small pill that floats up under the header and
 * fades whenever the player earns XP without levelling up — the lightweight feedback the
 * header ring alone doesn't give. Rendered once per gain by XpGainHost (keyed remount).
 */

import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { Animated, Easing, Platform, Text, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { XP_GAIN_FLASH, XP_GAIN_FLASH_TEST_ID } from '../../constants/celebrations.constants';
import { getXpGainFlashStyles } from './XpGainFlash.styles';

const USE_NATIVE = Platform.OS !== 'web';
const DESIGN_EASE = Easing.bezier(0.16, 1, 0.3, 1);

export interface XpGainFlashProps {
  amount: number;
  onDone: () => void;
  testID?: string;
}

export const XpGainFlash: React.FC<XpGainFlashProps> = ({ amount, onDone, testID }) => {
  const styles = useMemo(() => getXpGainFlashStyles(), []);
  const insets = useSafeAreaInsets();
  const opacity = useRef(new Animated.Value(0)).current;
  const translateY = useRef(new Animated.Value(0)).current;
  const doneRef = useRef(false);

  const finish = useCallback(() => {
    if (doneRef.current) return;
    doneRef.current = true;
    onDone();
  }, [onDone]);

  useEffect(() => {
    Animated.sequence([
      Animated.parallel([
        Animated.timing(opacity, { toValue: 1, duration: 220, useNativeDriver: USE_NATIVE }),
        Animated.timing(translateY, {
          toValue: -XP_GAIN_FLASH.TRAVEL,
          duration: XP_GAIN_FLASH.RISE_MS,
          easing: DESIGN_EASE,
          useNativeDriver: USE_NATIVE,
        }),
      ]),
      Animated.delay(XP_GAIN_FLASH.HOLD_MS),
      Animated.timing(opacity, {
        toValue: 0,
        duration: XP_GAIN_FLASH.FADE_MS,
        useNativeDriver: USE_NATIVE,
      }),
    ]).start(() => finish());
    // Mount-only: one fixed timeline per gain (host remounts via key).
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <View style={[styles.wrap, { top: insets.top + 52 }]} pointerEvents="none">
      <Animated.View
        style={[styles.pill, { opacity, transform: [{ translateY }] }]}
        testID={testID ?? XP_GAIN_FLASH_TEST_ID}
        accessibilityRole="text"
        accessibilityLabel={`Earned ${amount} XP`}
      >
        <Text style={styles.amount}>+{amount.toLocaleString()}</Text>
        <Text style={styles.unit}>XP</Text>
      </Animated.View>
    </View>
  );
};
